
import React, { useEffect, useMemo } from 'react';
import { ApolloLink, HttpLink, Observable } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { fromPromise } from '@apollo/client/link/utils';
import {
  ApolloClient,
  ApolloProvider,
  InMemoryCache,
} from '@apollo/client';
import { auth } from './firebase';
import { getAuthToken, extractInviteIdFromPath, removeAuthToken } from './utils';

// Create a singleton client instance
let apolloClient: ApolloClient<any> | null = null;
let currentInviteId: string | null = null;

function makeClient() {
  const httpLink = new HttpLink({
    uri: `${import.meta.env.VITE_GRAPHQL_HOST}/graphql`,
  });

  const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      for (const { message, locations, path } of graphQLErrors) {
        
        // Handle expired token errors
        if (message.match(/Firebase ID token has expired|auth\/id-token-expired/i)) {
          
          // Attempt to refresh the token
          if (auth.currentUser) {
            return fromPromise(
              auth.currentUser.getIdToken(true)
                .then((token) => {
                  // Update operation headers with new token
                  operation.setContext(({ headers = {} }) => ({
                    headers: {
                      ...headers,
                      Authorization: token ? `Bearer ${token}` : '',
                    },
                  }));
                  return token;
                })
                .catch((error) => {
                  // Token refresh failed - user needs to re-authenticate
                  removeAuthToken();
                  auth.signOut();
                  throw error;
                })
            ).flatMap(() => forward(operation));
          } else {
            removeAuthToken();
          }
        }
        
        // Handle other authentication errors
        if (message.match(/unauthenticated|unauthorized/i)) {
          removeAuthToken();
          auth.signOut();
        }
      }
    }

    if (networkError) {
      // Network error occurred
    }
  });

  const retryLink = new RetryLink({
    delay: {
      initial: 500,
      max: Infinity,
      jitter: true,
    },
    attempts: {
      max: 3,
      retryIf: (error) => !!error,
    },
  });

  const authLink = new ApolloLink((operation, forward) => {
    return fromPromise(
      (async () => {
        // localStorage is the fast path — populated by `onIdTokenChanged` and
        // by explicit `storeAuthToken` calls after sign-in. But that listener
        // is async, so mutations fired immediately after `signInWithCustomToken`
        // race against it. If the cache is empty but Firebase has a live
        // signed-in user, fall back to fetching the ID token directly so the
        // first request after sign-in doesn't go out unauthenticated.
        let token = getAuthToken();
        if (!token && auth.currentUser) {
          try {
            token = await auth.currentUser.getIdToken();
          } catch {
            // Network failure fetching token — proceed with no auth header
            // and let the resolver surface the error normally.
          }
        }
        operation.setContext({
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
          },
        });
        return token;
      })(),
    ).flatMap(() => forward(operation));
  });

  return new ApolloClient({
    cache: new InMemoryCache(),
    link: ApolloLink.from([errorLink, retryLink, authLink, httpLink]),
  });
}

function getClient() {
  const inviteId = extractInviteIdFromPath();
  
  // If invite ID changed, sign out Firebase user and clear client
  // BUT only if the user is anonymous (not a real authenticated user)
  if (inviteId !== currentInviteId) {
    
    // Only sign out if the current user is anonymous
    // Real authenticated users (Google/email sign-in) should persist across page loads
    const isAnonymous = auth.currentUser?.isAnonymous;
    
    if (isAnonymous) {
      // Sign out Firebase to force new anonymous user for new invite
      auth.signOut().then(() => {
        // Signed out previous anonymous user
      }).catch((error) => {
        // Error signing out
      });
      
      // Remove the old token
      removeAuthToken();
    }
    
    // Clear Apollo client regardless (to reset cache for new invitation)
    apolloClient = null;
    currentInviteId = inviteId;
  }
  
  // Create client if it doesn't exist
  if (!apolloClient) {
    apolloClient = makeClient();
  }
  
  return apolloClient;
}

export function ApolloWrapper({ children }: React.PropsWithChildren) {
  const inviteId = extractInviteIdFromPath();
  
  // Get client and recreate it if invite ID changes
  const client = useMemo(() => getClient(), [inviteId]);
  
  // Clear cache when invite ID changes
  useEffect(() => {
    if (client) {
      client.clearStore().catch((error) => {
        // Error clearing cache
      });
    }
  }, [inviteId, client]);
  
  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
